import React, { useState } from 'react';

const CareersPage: React.FC = () => {
  const [showCareerForm, setShowCareerForm] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<{title: string, category: string, location: string, experience: string, description?: string, responsibilities?: string[], requirements?: string[], type?: string} | null>(null);
  const [jobApplicationData, setJobApplicationData] = useState({
    name: '',
    phone: '',
    address: '',
    birthDate: '',
    experienceYears: '',
    expectedSalary: '',
    cvFile: null as File | null
  });
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    nationality: '',
    gender: '',
    maritalStatus: '',
    dateOfBirth: '',
    mobile1: '',
    mobile2: '',
    relativeMobile: '',
    availableFrom: '',
    homeAddress: '',
    appliedToRank: '',
    
    // Seaman Book Information
    seamanBookNo: '',
    profession: '',
    dateOfIssue: '',
    expiryDate: '',
    
    // Certificates
    securityAwareness: { available: false, expiry: '' },
    firstAid: { available: false, expiry: '' },
    fireFighting: { available: false, expiry: '' },
    personalSurvival: { available: false, expiry: '' },
    personalSafety: { available: false, expiry: '' },
    medicalCertificate: { available: false, expiry: '' },
    cocOfficers: { available: false, expiry: '' },
    gocDeck: { available: false, expiry: '' },
    deckEngineWatch: { available: false, expiry: '' },
    shipSecurity: { available: false, expiry: '' },
    foodHandler: { available: false, expiry: '' },
    
    // Sea Service Experience (12 rows by default)
    seaService: Array(12).fill(null).map(() => ({
      companyName: '',
      vesselName: '',
      vesselType: '',
      rank: '',
      from: '',
      to: '',
      experienceYears: 0
    }))
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCertificateChange = (cert: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [cert]: {
        ...prev[cert as keyof typeof prev] as any,
        [field]: value
      }
    }));
  };

  const calculateExperienceYears = (from: string, to: string): number => {
    if (!from || !to) return 0;
    
    const fromDate = new Date(from);
    const toDate = new Date(to);
    
    if (toDate <= fromDate) return 0;
    
    // Calculate years and months more precisely
    let years = toDate.getFullYear() - fromDate.getFullYear();
    let months = toDate.getMonth() - fromDate.getMonth();
    let days = toDate.getDate() - fromDate.getDate();
    
    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }
    
    // Convert to decimal: each month = 0.1 years (approximately)
    // More precise: 1 month ≈ 0.083 years, but using 0.1 as requested
    const totalYears = years + (months * 0.1);
    
    // Round to 1 decimal place
    return Math.round(totalYears * 10) / 10;
  };

  const handleSeaServiceChange = (index: number, field: string, value: string) => {
    setFormData(prev => {
      const newSeaService = prev.seaService.map((service, i) => {
        if (i === index) {
          const updatedService = { ...service, [field]: value };
          
          // Automatically calculate experience years when from or to date changes
          if (field === 'from' || field === 'to') {
            const fromDate = field === 'from' ? value : service.from;
            const toDate = field === 'to' ? value : service.to;
            updatedService.experienceYears = calculateExperienceYears(fromDate, toDate);
          }
          
          return updatedService;
        }
        return service;
      });

      return {
        ...prev,
        seaService: newSeaService
      };
    });
  };

  const addSeaServiceRow = () => {
    setFormData(prev => ({
      ...prev,
      seaService: [...prev.seaService, {
        companyName: '',
        vesselName: '',
        vesselType: '',
        rank: '',
        from: '',
        to: '',
        experienceYears: 0
      }]
    }));
  };

  const removeSeaServiceRow = (index: number) => {
    if (formData.seaService.length > 1) {
      setFormData(prev => ({
        ...prev,
        seaService: prev.seaService.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
    }
  };

  const handleJobApply = (job: any, category: string) => {
    const jobData = {
      title: job.title,
      category: category,
      location: job.location,
      experience: job.experience,
      description: job.description,
      responsibilities: job.responsibilities,
      requirements: job.requirements,
      type: job.type
    };
    
    setSelectedJob(jobData);
    
    if (job.type === 'form') {
      // Show the seafarer application form directly
      setShowCareerForm(true);
      setShowJobDetails(false);
      // Scroll to the form
      setTimeout(() => {
        const formElement = document.getElementById('career-application-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Show job details in full screen
      setShowJobDetails(true);
      setShowCareerForm(false);
    }
  };

  const handleApplyFromDetails = () => {
    // Open the job application modal
    setShowJobApplicationModal(true);
    setJobApplicationData({
      name: '',
      phone: '',
      address: '',
      birthDate: '',
      experienceYears: '',
      expectedSalary: '',
      cvFile: null
    });
  };

  const handleJobApplicationInputChange = (field: string, value: string) => {
    setJobApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobCvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setJobApplicationData(prev => ({
        ...prev,
        cvFile: file
      }));
    }
  };

  const handleJobApplicationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Job Application submitted:', {
      job: selectedJob,
      applicationData: jobApplicationData
    });
    alert(`Application for ${selectedJob?.title} submitted successfully! We will contact you soon.`);
    setShowJobApplicationModal(false);
    setShowJobDetails(false);
  };

  const closeModal = () => {
    setShowJobApplicationModal(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    console.log('CV file:', cvFile);
    alert('Application submitted successfully! We will contact you soon.');
  };

  // Job listings data
  const jobCategories = [
    {
      category: "Deck Department",
      jobs: [
        { 
          title: "Captain", 
          location: "Various Vessels", 
          experience: "10+ years", 
          posted: "2024-01-15",
          type: "details",
          description: "We are seeking an experienced Captain to lead our offshore vessel operations. The ideal candidate will have extensive experience in maritime operations and a proven track record of safety and efficiency.",
          responsibilities: [
            "Command and navigate offshore vessels",
            "Ensure compliance with maritime regulations and company policies",
            "Manage crew and vessel operations",
            "Maintain safety standards and emergency procedures",
            "Oversee cargo operations and vessel maintenance"
          ],
          requirements: [
            "Valid Master Mariner Certificate",
            "Minimum 10 years of experience as Captain",
            "Strong leadership and communication skills",
            "Knowledge of international maritime regulations",
            "Medical fitness certificate"
          ]
        },
        { 
          title: "Chief Officer", 
          location: "Offshore Vessels", 
          experience: "8+ years", 
          posted: "2024-01-10",
          type: "details",
          description: "Join our team as a Chief Officer on offshore vessels. This role requires strong navigational skills and the ability to support the Captain in all vessel operations.",
          responsibilities: [
            "Assist Captain in vessel navigation and operations",
            "Supervise deck crew and maintenance activities",
            "Manage cargo operations and safety procedures",
            "Maintain navigational equipment and charts",
            "Conduct safety drills and training"
          ],
          requirements: [
            "Chief Mate Certificate of Competency",
            "8+ years maritime experience",
            "Experience in offshore operations",
            "Strong organizational skills",
            "Valid STCW certificates"
          ]
        },
        { 
          title: "Second Officer", 
          location: "Supply Vessels", 
          experience: "5+ years", 
          posted: "2024-01-08",
          type: "form",
          description: "Opportunity for Second Officer position on supply vessels. Responsible for navigation watch and safety management."
        }
      ]
    },
    {
      category: "Engine Department", 
      jobs: [
        { 
          title: "Chief Engineer", 
          location: "PSV/AHTS", 
          experience: "10+ years", 
          posted: "2024-01-12",
          type: "details",
          description: "We are looking for a skilled Chief Engineer to manage all engineering operations on our Platform Supply Vessels and Anchor Handling Tug Supply vessels.",
          responsibilities: [
            "Oversee all engine room operations",
            "Manage engineering crew and maintenance schedules",
            "Ensure compliance with environmental regulations",
            "Supervise fuel management and consumption",
            "Conduct technical inspections and repairs"
          ],
          requirements: [
            "Chief Engineer Certificate",
            "10+ years experience in maritime engineering",
            "Experience with PSV/AHTS vessels",
            "Strong technical and management skills",
            "Valid STCW and medical certificates"
          ]
        },
        { 
          title: "Second Engineer", 
          location: "Various Vessels", 
          experience: "6+ years", 
          posted: "2024-01-05",
          type: "form",
          description: "Seeking qualified Second Engineer for various vessel types. Must have strong technical skills and experience in marine engineering."
        },
        { 
          title: "Third Engineer", 
          location: "Offshore Vessels", 
          experience: "4+ years", 
          posted: "2024-01-03",
          type: "form",
          description: "Third Engineer position available for offshore operations. Responsible for maintenance and operation of engineering systems."
        }
      ]
    },
    {
      category: "General Purpose",
      jobs: [
        { 
          title: "AB Seaman", 
          location: "All Vessels", 
          experience: "2+ years", 
          posted: "2024-01-14",
          type: "form",
          description: "Able Seaman position available across our fleet. Must be certified and experienced in deck operations."
        },
        { 
          title: "Ordinary Seaman", 
          location: "Training Vessels", 
          experience: "Entry level", 
          posted: "2024-01-11",
          type: "form",
          description: "Entry-level position for Ordinary Seaman. Training will be provided for the right candidate."
        },
        { 
          title: "Cook", 
          location: "All Vessels", 
          experience: "3+ years", 
          posted: "2024-01-09",
          type: "form",
          description: "Experienced Cook needed for vessel operations. Must be capable of preparing meals for crew in maritime environment."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Careers & Opportunities</h1>
        
        {/* Job Listings Section */}
        <div className="maritime-card p-8 mb-12">
          <h2 className="text-3xl font-bold text-ocean-800 mb-8 text-center">Current Job Openings</h2>
          
          <div className="space-y-8">
            {jobCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-ocean-600 text-white px-6 py-3">
                  <h3 className="text-xl font-semibold">{category.category}</h3>
                </div>
                <div className="p-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.jobs.map((job, jobIndex) => (
                      <div key={jobIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-ocean-300 transition-colors">
                        <h4 className="font-semibold text-ocean-800 text-lg mb-2">{job.title}</h4>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            {job.location}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            {job.experience}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            Posted: {new Date(job.posted).toLocaleDateString()}
                          </div>
                        </div>
                        <button 
                          onClick={() => handleJobApply(job, category.category)}
                          className="mt-3 w-full bg-ocean-500 hover:bg-ocean-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Apply Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Job Details Full Screen */}
        {showJobDetails && selectedJob && (
          <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100 z-50 overflow-y-auto">
            <div className="min-h-screen py-8 px-4">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-xl p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-3xl font-bold text-ocean-800 mb-2">{selectedJob.title}</h2>
                      <p className="text-lg text-ocean-600">{selectedJob.category}</p>
                    </div>
                    <button
                      onClick={() => setShowJobDetails(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-3 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Location:</strong> {selectedJob.location}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-3 text-ocean-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Experience:</strong> {selectedJob.experience}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-ocean-800 mb-3">Job Description</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedJob.description}</p>
                  </div>

                  {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-ocean-800 mb-3">Key Responsibilities</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {selectedJob.responsibilities.map((resp, index) => (
                          <li key={index}>{resp}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-ocean-800 mb-3">Requirements</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {selectedJob.requirements.map((req, index) => (
                          <li key={index}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="bg-ocean-50 border-l-4 border-ocean-500 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-ocean-800 mb-3">Ready to Apply?</h3>
                    <p className="text-gray-700 mb-4">
                      Click the button below to submit your application for this position.
                    </p>
                    <button
                      onClick={handleApplyFromDetails}
                      className="btn-primary px-8 py-3"
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Career Application Toggle Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowCareerForm(!showCareerForm)}
            className="btn-primary px-8 py-4 text-lg flex items-center mx-auto"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {showCareerForm ? 'Hide Application Form' : 'Show Application Form'}
          </button>
        </div>

        {/* Seafarer Application Form */}
        {showCareerForm && (
        <div id="career-application-form" className="maritime-card p-8 mb-12">
          <div className="flex items-center justify-center mb-8">
            <img src="/logo.jpg" alt="PetroMarine Logo" className="h-16 w-auto mr-4" />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-ocean-800">Seafarer Application</h2>
              <p className="text-gray-600">PetroMarine Offshore Projects Support Co.</p>
              {selectedJob && (
                <p className="text-ocean-600 font-medium mt-2">
                  Applying for: {selectedJob.title} - {selectedJob.category}
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* CV Upload Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">CV Upload / تحميل السيرة الذاتية</h3>
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Upload your CV (PDF, DOC, DOCX) / تحميل السيرة الذاتية <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100 cursor-pointer border border-gray-300 rounded-lg p-2"
                    required
                  />
                  {cvFile && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected: {cvFile.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Personal Information</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name / الاسم <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality / الجنسية
                  </label>
                  <input
                    type="text"
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender / الجنس
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marital Status / الحالة الاجتماعية
                  </label>
                  <select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth / تاريخ الميلاد
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile 1 / الهاتف الأول
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile1}
                    onChange={(e) => handleInputChange('mobile1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile 2 / الهاتف الثاني
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile2}
                    onChange={(e) => handleInputChange('mobile2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Relative Mobile / هاتف الأقارب
                  </label>
                  <input
                    type="tel"
                    value={formData.relativeMobile}
                    onChange={(e) => handleInputChange('relativeMobile', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available from / متاح من تاريخ
                  </label>
                  <input
                    type="date"
                    value={formData.availableFrom}
                    onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applied to rank / الوظيفة المقدم لها <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.appliedToRank}
                    onChange={(e) => handleInputChange('appliedToRank', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="e.g., Captain, Chief Engineer, AB Seaman"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Home Address / عنوان المنزل
                </label>
                <textarea
                  value={formData.homeAddress}
                  onChange={(e) => handleInputChange('homeAddress', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Seaman Book Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Marine Passport / الجواز البحري</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marine Passport No. / رقم الجواز البحري
                  </label>
                  <input
                    type="text"
                    value={formData.seamanBookNo}
                    onChange={(e) => handleInputChange('seamanBookNo', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profession / المهنة بالجواز البحري
                  </label>
                  <input
                    type="text"
                    value={formData.profession}
                    onChange={(e) => handleInputChange('profession', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Issue / تاريخ الإصدار
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfIssue}
                    onChange={(e) => handleInputChange('dateOfIssue', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date / تاريخ الانتهاء
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Certificates Section */}
            <div className="border-b border-gray-200 pb-8">
              <h3 className="text-xl font-semibold text-ocean-800 mb-6">Certificates / الشهادات</h3>
              <div className="space-y-4">
                {[
                  { key: 'securityAwareness', label: 'Security Awareness / التوعية الأمنية' },
                  { key: 'firstAid', label: 'First Aid / Medical Studies / اسعافات الطوارئ' },
                  { key: 'fireFighting', label: 'Fire Fighting / مكافحة الحرائق' },
                  { key: 'personalSurvival', label: 'Personal Sea Survival Techniques / تقنيات البقاء والإنقاذ والنجاة' },
                  { key: 'personalSafety', label: 'Personal Safety and Social Responsibilities / السلامة الشخصية والمسؤولية الاجتماعية' },
                  { key: 'medicalCertificate', label: 'International Medical Certificate for Seafarers / الشهادة الطبية البحرية' },
                  { key: 'cocOfficers', label: 'COC (Officers only) / شهادة الكفاءة' },
                  { key: 'gocDeck', label: 'GOC (Deck Officers only) / شهادة الخبرة بحرس وحفظ واستبصاف' },
                  { key: 'deckEngineWatch', label: 'Deck/Engine Watch Keeping Certificate (For Bosun, AB & Mechanics) / شهادة الخبرة بحرس ومتابعة واستبصاف' },
                  { key: 'shipSecurity', label: 'Ship Security Officer & Rescue Boats (For Bosun, AB & Mechanics) / ضابط أمن السفن وقوارب الإنقاذ' },
                  { key: 'foodHandler', label: 'Food Handler Certificate (For Cook) / شهادات تناول الطعام / الطباخ' }
                ].map((cert) => (
                  <div key={cert.key} className="grid md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
                    <div className="md:col-span-1">
                      <label className="text-sm font-medium text-gray-700">{cert.label}</label>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`${cert.key}_available`}
                          checked={(formData as any)[cert.key]?.available === true}
                          onChange={() => handleCertificateChange(cert.key, 'available', true)}
                          className="mr-2"
                        />
                        Yes / نعم
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`${cert.key}_available`}
                          checked={(formData as any)[cert.key]?.available === false}
                          onChange={() => handleCertificateChange(cert.key, 'available', false)}
                          className="mr-2"
                        />
                        No / لا
                      </label>
                    </div>
                    <div>
                      <input
                        type="date"
                        placeholder="Expiry Date / تاريخ الانتهاء"
                        value={(formData as any)[cert.key]?.expiry || ''}
                        onChange={(e) => handleCertificateChange(cert.key, 'expiry', e.target.value)}
                        disabled={!(formData as any)[cert.key]?.available}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sea Service Experience Section */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-ocean-800">Sea Service Experience / الخبرات البحرية</h3>
                <button
                  type="button"
                  onClick={addSeaServiceRow}
                  className="bg-ocean-500 hover:bg-ocean-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Row / إضافة صف
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-ocean-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Company Name / اسم الشركة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Vessel Name / اسم السفينة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Vessel Type / نوع السفينة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Rank / الرتبة</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">From / من</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">To / إلى</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Experience (Years) / الخبرة (سنوات)</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Action / إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.seaService.map((service, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.companyName}
                            onChange={(e) => handleSeaServiceChange(index, 'companyName', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.vesselName}
                            onChange={(e) => handleSeaServiceChange(index, 'vesselName', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.vesselType}
                            onChange={(e) => handleSeaServiceChange(index, 'vesselType', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={service.rank}
                            onChange={(e) => handleSeaServiceChange(index, 'rank', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={service.from}
                            onChange={(e) => handleSeaServiceChange(index, 'from', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={service.to}
                            onChange={(e) => handleSeaServiceChange(index, 'to', e.target.value)}
                            className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-ocean-500"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <div className="text-center font-medium text-ocean-600">
                            {service.experienceYears > 0 ? `${service.experienceYears} years` : '-'}
                          </div>
                        </td>
                        <td className="border border-gray-300 p-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeSeaServiceRow(index)}
                            disabled={formData.seaService.length <= 1}
                            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            title="Delete row / حذف الصف"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center pt-8">
              <button
                type="submit"
                className="btn-primary px-12 py-4 text-lg"
              >
                Submit Application / تقديم الطلب
              </button>
            </div>
          </form>
        </div>
        )}
        
        <div className="maritime-card p-8 mb-12 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Join Our Team</h2>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-6">
            PetroMarine offers exciting career opportunities for maritime professionals. 
            We are always looking for qualified seafarers who share our commitment to safety, 
            quality, and environmental protection.
          </p>
          <div className="bg-ocean-50 border-l-4 border-ocean-500 p-4 max-w-3xl mx-auto">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-ocean-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-ocean-700 font-medium">
                  <strong>Application Required:</strong> All candidates must complete the seafarer application form below to be considered for employment.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Competitive Benefits</h3>
            <p className="text-gray-600 text-sm">Attractive salary packages and comprehensive benefits</p>
          </div>

          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Career Growth</h3>
            <p className="text-gray-600 text-sm">Professional development and advancement opportunities</p>
          </div>

          <div className="maritime-card p-6 text-center">
            <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-ocean-800 mb-2">Modern Fleet</h3>
            <p className="text-gray-600 text-sm">Work with state-of-the-art vessels and equipment</p>
          </div>
        </div>

        {/* Job Application Modal */}
        {showJobApplicationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-ocean-600 text-white p-4 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold">Apply for Position</h3>
                    <p className="text-sm opacity-90">{selectedJob?.title} - {selectedJob?.category}</p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={handleJobApplicationSubmit} className="p-6 space-y-4">
                {/* CV Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CV File <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleJobCvUpload}
                    required
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100 cursor-pointer border border-gray-300 rounded-lg p-2"
                  />
                  {jobApplicationData.cvFile && (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected: {jobApplicationData.cvFile.name}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={jobApplicationData.name}
                    onChange={(e) => handleJobApplicationInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={jobApplicationData.phone}
                    onChange={(e) => handleJobApplicationInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={jobApplicationData.address}
                    onChange={(e) => handleJobApplicationInputChange('address', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Enter your address"
                  />
                </div>

                {/* Birth Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={jobApplicationData.birthDate}
                    onChange={(e) => handleJobApplicationInputChange('birthDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                {/* Experience Years */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Years <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="50"
                    value={jobApplicationData.experienceYears}
                    onChange={(e) => handleJobApplicationInputChange('experienceYears', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Years of experience"
                  />
                </div>

                {/* Expected Salary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Salary (USD) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={jobApplicationData.expectedSalary}
                    onChange={(e) => handleJobApplicationInputChange('expectedSalary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                    placeholder="Expected monthly salary"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-ocean-500 hover:bg-ocean-600 text-white rounded-lg transition-colors duration-200"
                  >
                    Submit Application
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CareersPage;